import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  "data-test": "revenue-report",
  class: "revenue-report"
}
const _hoisted_2 = { class: "revenue-report__toolbar" }
const _hoisted_3 = { class: "revenue-report__controls" }
const _hoisted_4 = { class: "revenue-report__grouping" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  "data-test": "revenue-error",
  class: "revenue-report__error"
}
const _hoisted_7 = {
  "data-test": "revenue-kpis",
  class: "revenue-report__kpis"
}
const _hoisted_8 = {
  key: 0,
  class: "flex justify-center py-8"
}

import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BasicSpinner from '@/components/BasicSpinner.vue'
import CustomerProductsOrdered from '@/components/customers/CustomerProductsOrdered.vue'
import StatTile from '@/components/customers/record/StatTile.vue'
import RevenuePeriodTable from '@/components/customers/RevenuePeriodTable.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import ReportChart from '@/components/reports/ReportChart.vue'
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter.vue'
import { averageOrder, revenueSummaryTiles, useCustomerRevenue } from '@/composables/useCustomerRevenue'
import { useReportDateRange } from '@/composables/useReportDateRange'
import { customerProfilePath } from '@/config/customerTabs'
import { reportDetailByPath } from '@/config/reportGroups'
import { downloadCsv } from '@/helpers/csv'
import { DATE_RANGE_OPTIONS } from '@/helpers/reportDateRanges'
import { DEFAULT_REVENUE_RANGE, normalizeRangeValue, rangeFromQuery, revenueRangeQuery } from '@/helpers/revenueRange'
import { bucketRevenueRows, revenueChartData } from '@/services/reportParser.service'


export default {
  __name: 'CustomerRevenueReport',
  setup(__props) {

const GROUP_BY_OPTIONS = reportDetailByPath['/revenue-summary'].dropdownOptions.map(({ label, value }) => ({
  name: label,
  value
}))

const CSV_HEADER = ['Period', 'Orders', 'Gross', 'Refunds', 'Net']

const route = useRoute()
const router = useRouter()

const customerId = computed(() => String(route.params.id))
const profilePath = computed(() => customerProfilePath({ id: customerId.value, listId: route.params.listid }))

const { selectedDateRange, selectedDateRangeLabel, customDateRange, setDateRange, apiDateRange } =
  useReportDateRange(DEFAULT_REVENUE_RANGE)
setDateRange(rangeFromQuery(route.query))
const apiRange = computed(() => apiDateRange())
const groupBy = ref('month')

const { rows, loading, failed, totals } = useCustomerRevenue(customerId, apiRange)

const periods = computed(() => bucketRevenueRows(rows.value, groupBy.value))
const periodsNewestFirst = computed(() => [...periods.value].reverse())
const chartData = computed(() => revenueChartData(periods.value, ['gross']))
const chartTitle = computed(() => `Gross revenue by ${groupBy.value}`)

const kpis = computed(() => [
  ...revenueSummaryTiles(totals.value),
  { label: 'Paid orders', value: String(totals.value.orders) },
  { label: 'Avg order', value: averageOrder(totals.value) }
])

const onRangeChange = value => {
  setDateRange(normalizeRangeValue(value))
  router.replace({ query: revenueRangeQuery({ preset: selectedDateRange.value, apiRange: apiRange.value }) })
}

const queryOf = query => ({ range: query.range, from: query.from, to: query.to })
const sameQuery = (a, b) => a.range === b.range && a.from === b.from && a.to === b.to
const currentQuery = () => queryOf(revenueRangeQuery({ preset: selectedDateRange.value, apiRange: apiRange.value }))

watch(
  () => route.query,
  query => {
    if (!sameQuery(queryOf(query), currentQuery())) setDateRange(rangeFromQuery(query))
  }
)

watch(customerId, () => {
  groupBy.value = 'month'
})

const exportCsv = () => {
  const { beginning_date: from, ending_date: to } = apiRange.value
  downloadCsv(`revenue-${customerId.value}-${from}-to-${to}.csv`, [
    CSV_HEADER,
    ...periodsNewestFirst.value.map(period => [
      period.key,
      period.orders,
      period.gross.toFixed(2),
      period.refunds.toFixed(2),
      period.net.toFixed(2)
    ])
  ])
}

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_router_link, {
        "data-test": "back-to-profile",
        to: profilePath.value,
        class: "revenue-report__back"
      }, {
        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("← Profile", -1)
        ]))]),
        _: 1
      }, 8, ["to"]),
      _cache[2] || (_cache[2] = _createElementVNode("h2", { class: "revenue-report__title" }, "Revenue", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(ReportDateRangeFilter, {
          options: _unref(DATE_RANGE_OPTIONS),
          selected: _unref(selectedDateRange),
          "selected-label": _unref(selectedDateRangeLabel),
          "custom-range": _unref(customDateRange),
          onChange: onRangeChange
        }, null, 8, ["options", "selected", "selected-label", "custom-range"]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(SelectInput, {
            pill: true,
            options: _unref(GROUP_BY_OPTIONS),
            "model-value": groupBy.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = value => (groupBy.value = value))
          }, null, 8, ["options", "model-value"])
        ]),
        _createElementVNode("button", {
          type: "button",
          "data-test": "export-csv",
          class: "revenue-report__export",
          disabled: _unref(loading) || periods.value.length === 0,
          onClick: exportCsv
        }, " Export CSV ", 8, _hoisted_5)
      ])
    ]),
    (!_unref(loading) && _unref(failed))
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, " Couldn't load revenue. Please try again. "))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_7, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(kpis.value, (kpi) => {
              return (_openBlock(), _createBlock(StatTile, {
                key: kpi.label,
                class: "revenue-report__kpi",
                label: kpi.label,
                value: kpi.value,
                loading: _unref(loading)
              }, null, 8, ["label", "value", "loading"]))
            }), 128))
          ]),
          _createVNode(ReportChart, {
            "report-id": "customer-revenue",
            title: chartTitle.value,
            "chart-data": chartData.value,
            "chart-type": "line",
            loading: _unref(loading),
            "currency-format": true,
            "table-hover-enabled": false,
            "empty-state-title": "No sales in this period",
            gridlines: true
          }, null, 8, ["title", "chart-data", "loading"]),
          (_unref(loading))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createVNode(BasicSpinner, { color: "gray-900" })
              ]))
            : (_openBlock(), _createBlock(RevenuePeriodTable, {
                key: 1,
                periods: periodsNewestFirst.value,
                "group-by": groupBy.value
              }, null, 8, ["periods", "group-by"]))
        ], 64)),
    (_openBlock(), _createBlock(CustomerProductsOrdered, {
      key: customerId.value,
      "conversation-user-pair-id": customerId.value,
      range: apiRange.value,
      title: "Products in this period"
    }, null, 8, ["conversation-user-pair-id", "range"]))
  ]))
}
}

}